import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Briefcase, GraduationCap, Sparkles, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Field } from "@/components/ui/Label";
import { PageHeader } from "@/components/layout/PageHeader";
import { useFamilyProfile } from "@/hooks/useProfile";
import { useTaxProjection } from "@/hooks/useTax";
import { formatMoney, formatPercent } from "@/lib/utils";

const STANDARD_DEDUCTION_2025 = 15_000;
const WI_EDVEST_PER_BENEFICIARY = 4_260;

export function HireFamilyPage() {
  const family = useFamilyProfile();
  const projection = useTaxProjection();
  const [wagePerKid, setWagePerKid] = useState("15000");
  const [spouseWage, setSpouseWage] = useState("14000");

  const childCount = family.data?.children.length ?? 0;
  const eligibleKids = family.data?.children.filter((c) => age(c.dateOfBirth) < 18).length ?? 0;
  const onPayrollKids = family.data?.children.filter((c) => c.isOnPayroll).length ?? 0;

  const proj = projection.data?.projection;
  const blocked = proj?.hasBlockingGaps ?? false;
  const fedRate = blocked ? null : proj?.nextDollarFederalRate ?? null;
  const stateRate = blocked ? null : proj?.nextDollarStateRate ?? null;
  const combined =
    fedRate != null && stateRate != null ? fedRate + stateRate : null;

  const kidWage = Number(wagePerKid) || 0;
  const spouseW = Number(spouseWage) || 0;

  const kidsAnnualWages = kidWage * eligibleKids;
  const taxFreeKidPortion = Math.min(kidWage, STANDARD_DEDUCTION_2025) * eligibleKids;
  const kidsBusinessDeduction =
    combined != null ? kidsAnnualWages * combined : null;
  const kidsRothCapacity = useMemo(
    () => Math.min(kidWage, 7_000) * eligibleKids,
    [kidWage, eligibleKids],
  );

  const spouseTaxBenefit = spouseW * 0.10; // conservative benefit estimate (Roth IRA + 401k room)
  const spouseRothIra = Math.min(spouseW, 7_000);

  const edvestDeduction =
    stateRate != null ? childCount * WI_EDVEST_PER_BENEFICIARY * stateRate : null;

  return (
    <>
      <PageHeader
        eyebrow="Strategy / Hire Family"
        title="Spouse + kids on payroll"
        description="Move income from the top bracket to lower-bracket family members. Most powerful while the entity is a sole-prop or FMLLC (FICA-exempt for kids)."
        actions={
          <Badge tone="info">
            {onPayrollKids} of {eligibleKids} eligible kids on payroll
          </Badge>
        }
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Estimated annual benefit
          </CardTitle>
          <CardDescription>
            Assumes wages are documented, reasonable, and paid through a real payroll system.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Wage per child / yr">
            <Input
              type="number"
              inputMode="decimal"
              value={wagePerKid}
              onChange={(e) => setWagePerKid(e.target.value)}
            />
          </Field>
          <Field label="Spouse W-2 wage / yr">
            <Input
              type="number"
              inputMode="decimal"
              value={spouseWage}
              onChange={(e) => setSpouseWage(e.target.value)}
            />
          </Field>
          <div className="flex items-end">
            <Link to="/people">
              <Button variant="outline">Edit family in People</Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Briefcase className="h-4 w-4 text-primary" />
              Hire kids
            </CardTitle>
            <CardDescription>{eligibleKids} eligible (under 18)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Wages paid" value={formatMoney(kidsAnnualWages)} />
            <Row label="Tax-free to child portion" value={formatMoney(taxFreeKidPortion)} />
            <Row
              label="Business deduction value"
              value={
                kidsBusinessDeduction != null
                  ? formatMoney(kidsBusinessDeduction)
                  : "—"
              }
              hint={
                combined != null ? `@ ${formatPercent(combined, 2)}` : "Add intake"
              }
            />
            <Row label="Custodial Roth IRA capacity" value={formatMoney(kidsRothCapacity)} />
            <p className="pt-2 text-xs text-muted-foreground">
              Real services, real W-2, real bank account. Document hours, role, and pay scale.
              Wages above standard deduction are taxable to the child at their bracket (likely 0-10%).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-primary" />
              Hire spouse
            </CardTitle>
            <CardDescription>
              {family.data?.hasSpouse ? "Spouse profile present" : "No spouse on profile"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Suggested wage" value={formatMoney(spouseW)} />
            <Row label="Roth IRA capacity (limit $7k)" value={formatMoney(spouseRothIra)} />
            <Row
              label="Approx tax-shift benefit"
              value={formatMoney(spouseTaxBenefit)}
              hint="Conservative: assumes 10% net household savings on the shifted income"
            />
            <p className="pt-2 text-xs text-muted-foreground">
              Hiring a spouse opens 401(k) coverage if you set up a Solo 401(k) variant, and unlocks
              spousal Roth IRA contributions when household earned income limits it.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <GraduationCap className="h-4 w-4 text-primary" />
              Edvest 529 (WI deduction)
            </CardTitle>
            <CardDescription>
              {family.data?.stateOfResidence === "WI"
                ? `${childCount} ${childCount === 1 ? "child" : "children"} in WI`
                : "Available only when WI is state of residence"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row
              label="Deduction per beneficiary"
              value={formatMoney(WI_EDVEST_PER_BENEFICIARY)}
            />
            <Row
              label="Total household contribution capacity"
              value={formatMoney(WI_EDVEST_PER_BENEFICIARY * childCount)}
            />
            <Row
              label="State tax saved"
              value={
                edvestDeduction != null ? formatMoney(edvestDeduction) : "—"
              }
              hint={stateRate != null ? `@ ${formatPercent(stateRate)}` : "Add intake"}
            />
            <p className="pt-2 text-xs text-muted-foreground">
              Pair with kid wages: pay the kid → kid contributes to a custodial Roth IRA → 529
              funds primary expenses. Roth has tax-free growth + flexible later use.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Compliance reminders for hire-family</CardTitle>
          <CardDescription>The IRS scrutinizes family payroll. Defend it pre-emptively.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>· Real W-2, real timesheet, real role description, real direct-deposit to the child&apos;s bank.</p>
          <p>· Wage must be reasonable for the work and the local market.</p>
          <p>· Sole-prop &amp; FMLLC: kids&apos; wages are FICA + FUTA exempt under age 18 / 21 respectively.</p>
          <p>· S-Corp: kids&apos; wages lose the FICA exemption — that&apos;s why FMLLC matters if you elect S-Corp.</p>
          <p>· Wisconsin: also state-tax exempt for the child up to the WI standard deduction.</p>
        </CardContent>
      </Card>
    </>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/40 pb-1.5 last:border-b-0">
      <span className="text-muted-foreground">
        {label}
        {hint && <span className="ml-1 text-xs">({hint})</span>}
      </span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function age(iso: string) {
  const dob = new Date(iso);
  if (Number.isNaN(dob.getTime())) return 99;
  const now = new Date();
  let a = now.getFullYear() - dob.getFullYear();
  if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) {
    a--;
  }
  return a;
}
